import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Permission } from './usePermissions';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface Role {
  id: string;
  name: string;
  description?: string;
  organizationId: string | null; // null for global/default roles
  isGlobal: boolean; // Global/default roles (system-defined)
  isMutable: boolean; // Can this role be edited/deleted
  createdAt?: string;
  updatedAt?: string;
}

interface RolesListResponse {
  roles: Role[];
}

interface RoleWithPermissionsResponse {
  role: Role;
  permissions: Permission[];
}

const backendApi = config.backendApi;

const fetchRoles = async (): Promise<Role[]> => {
  const response = await axios.get<RolesListResponse>(`${backendApi}/api/roles`, { withCredentials: true });
  return response.data.roles;
};

const fetchRolePermissions = async (roleId: string): Promise<Permission[]> => {
  const response = await axios.get<RoleWithPermissionsResponse>(
    `${backendApi}/api/roles/${roleId}/permissions`,
    { withCredentials: true }
  );
  return response.data.permissions;
};

export function useRoles(organizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Role[]>({
    queryKey: ['roles', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch roles.");
      }
      return fetchRoles();
    },
    enabled: !!organizationId && contextReady,
  });
}

export function useRolePermissions(roleId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Permission[]>({
    queryKey: ['roles', roleId, 'permissions'],
    queryFn: () => {
      if (!roleId) {
        throw new Error("roleId is required to fetch role permissions.");
      }
      return fetchRolePermissions(roleId);
    },
    enabled: !!roleId && contextReady,
  });
}
